/**
 * 필드 렌더러 — 명세 하나를 화면 위젯 하나로.
 *
 * 모든 입력이 여기를 지나므로 글자수 표시·오류 표시·비활성 처리가 한 번만 구현된다.
 * 템플릿이 늘어도 이 파일은 그대로다.
 */
import { useMemo, type ReactNode } from 'react';
import { icon as renderIcon, type IconName } from '@signageone/core';
import { appendItem, getPath, moveItem, removeItem, setPath } from './paths.js';
import { ICON_OPTIONS, type Field } from './spec.js';
import { HelpTip } from '../HelpTip.js';
import { DateField, DateTimeField } from '../DateField.js';
import { TimeField } from '../TimeField.js';
import { NumberInput, numberProblem } from './NumberInput.js';
import { MediaField, readMediaValue, type MediaFieldHost } from './MediaField.js';
import { ZonesField } from './ZonesField.js';

export interface FieldContext {
  /** 편집 중인 전체 데이터. */
  value: unknown;
  onChange: (next: unknown) => void;
  /** 경로별 오류 메시지. 서버가 준 zod 이슈를 매핑한 것. */
  errors: ReadonlyMap<string, string>;
  disabled: boolean;
  /** 그림·영상 칸이 쓰는 콘솔 기능(고르기·올리기) — 없으면 그 칸은 보기만 한다. */
  media?: MediaFieldHost;
}

/** 상위 경로를 붙여 자식 필드의 절대 경로를 만든다. */
function join(prefix: string, path: string): string {
  return prefix ? `${prefix}.${path}` : path;
}

export function FieldView({
  field,
  prefix,
  ctx,
}: {
  field: Field;
  prefix: string;
  ctx: FieldContext;
}): ReactNode {
  const path = join(prefix, field.path);
  const raw = getPath(ctx.value, path);
  const error = ctx.errors.get(path);

  const update = (next: unknown): void => ctx.onChange(setPath(ctx.value, path, next));

  switch (field.kind) {
    case 'text':
      return (
        <Labeled label={field.label} help={field.help} error={error} count={countOf(raw, field.max)}>
          <input
            className="input"
            value={asString(raw)}
            maxLength={field.max}
            placeholder={field.placeholder}
            disabled={ctx.disabled}
            onChange={(event) => update(event.target.value)}
          />
        </Labeled>
      );

    case 'textarea':
      return (
        <Labeled label={field.label} help={field.help} error={error} count={countOf(raw, field.max)}>
          <textarea
            className="input input-textarea"
            rows={3}
            value={asString(raw)}
            maxLength={field.max}
            disabled={ctx.disabled}
            onChange={(event) => update(event.target.value)}
          />
        </Labeled>
      );

    case 'number': {
      /*
       * 지우면 빈 칸이다(값을 지운다) — 예전에는 0 으로 되돌아가 지우고 새로 칠 수가 없었다. 비었거나 범위를 벗어나면
       * 스키마 검증이 저장을 막고, 그 까닭은 우리 말로 칸 아래에 적는다(스키마의 영어 문장 대신).
       */
      const value = typeof raw === 'number' ? raw : null;
      const rule = { label: field.label, min: field.min ?? 0, max: field.max, integer: false };
      const problem = error ? (numberProblem(value, rule) ?? error) : undefined;
      return (
        <Labeled label={field.label} help={field.help} error={problem}>
          <NumberInput
            rule={rule}
            value={value}
            invalid={Boolean(problem)}
            disabled={ctx.disabled}
            onChange={(next) => update(next ?? undefined)}
          />
        </Labeled>
      );
    }

    case 'boolean':
      return (
        <label className="checkbox checkbox-field">
          <input
            type="checkbox"
            checked={raw === true}
            disabled={ctx.disabled}
            onChange={(event) => update(event.target.checked)}
          />
          {field.label}
          {field.help ? <HelpTip text={field.help} label={`${field.label} 도움말`} /> : null}
        </label>
      );

    case 'time':
      return (
        <Labeled label={field.label} help={field.help} error={error}>
          {/* 24시간 글자 칸 + 시·분 목록 — 브라우저 시각 칸은 좁은 칸에서 "오후 04:12" 가 잘린다. */}
          <TimeField value={asString(raw)} label={field.label} disabled={ctx.disabled} onChange={update} />
        </Labeled>
      );

    case 'datetime':
      return (
        <Labeled label={field.label} help={field.help} error={error}>
          <div className="field-row">
            {/* 마감 같은 끝 시각이라 날짜를 처음 고르면 그날 23:59 로 채운다. */}
            <DateTimeField
              value={toLocalInput(raw)}
              label={field.label}
              defaultTime="23:59"
              clearable={false}
              disabled={ctx.disabled}
              onChange={(next) => update(next ? new Date(next).toISOString() : null)}
            />
            {raw ? (
              <button type="button" className="link" disabled={ctx.disabled} onClick={() => update(null)}>
                지우기
              </button>
            ) : null}
          </div>
        </Labeled>
      );

    case 'date':
      return (
        <Labeled label={field.label} help={field.help} error={error}>
          {/* 년·월·일이 다 보이는 날짜 칸 + 달력(`DateField`) — 브라우저 날짜 칸은 좁은 칸에서 일이 잘렸다. */}
          <DateField value={asString(raw)} label={field.label} disabled={ctx.disabled} onChange={update} />
        </Labeled>
      );

    case 'select':
      return (
        <Labeled label={field.label} help={field.help} error={error}>
          <select
            className="input"
            value={asString(raw)}
            disabled={ctx.disabled}
            onChange={(event) => update(event.target.value)}
          >
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Labeled>
      );

    case 'icon':
      return (
        <Labeled label={field.label} error={error}>
          <div className="field-row">
            <IconGlyph name={asString(raw) as IconName} />
            <select
              className="input"
              value={asString(raw)}
              disabled={ctx.disabled}
              onChange={(event) => update(event.target.value)}
            >
              {ICON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </Labeled>
      );

    case 'media':
      return (
        <MediaField
          label={field.label}
          help={field.help}
          error={error ?? ctx.errors.get(`${path}.assetId`)}
          value={readMediaValue(raw)}
          host={ctx.media}
          disabled={ctx.disabled}
          onChange={update}
        />
      );

    case 'zones':
      return (
        <ZonesField
          label={field.label}
          help={field.help}
          value={raw}
          errors={ctx.errors}
          host={ctx.media}
          disabled={ctx.disabled}
          onChange={update}
        />
      );

    case 'csv':
      return (
        <Labeled label={field.label} help={field.help} error={error}>
          <input
            className="input"
            value={Array.isArray(raw) ? raw.join(', ') : ''}
            disabled={ctx.disabled}
            onChange={(event) => update(parseCsv(event.target.value, field.numeric === true))}
          />
        </Labeled>
      );

    case 'stringList':
      return (
        <ListEditor
          label={field.label}
          help={field.help}
          itemLabel={field.itemLabel}
          count={lengthOf(raw)}
          max={field.max}
          disabled={ctx.disabled}
          onAdd={() => ctx.onChange(appendItem(ctx.value, path, ''))}
          onRemove={(index) => ctx.onChange(removeItem(ctx.value, path, index))}
          onMove={(index, delta) => ctx.onChange(moveItem(ctx.value, path, index, delta))}
          renderItem={(index) => {
            const itemPath = `${path}.${index}`;
            return (
              <>
                <input
                  className="input"
                  value={asString(getPath(ctx.value, itemPath))}
                  maxLength={field.itemMax}
                  disabled={ctx.disabled}
                  onChange={(event) => ctx.onChange(setPath(ctx.value, itemPath, event.target.value))}
                />
                {ctx.errors.get(itemPath) ? (
                  <p className="field-error">{ctx.errors.get(itemPath)}</p>
                ) : null}
              </>
            );
          }}
        />
      );

    case 'objectList':
      return (
        <ListEditor
          label={field.label}
          help={field.help}
          itemLabel={field.itemLabel}
          count={lengthOf(raw)}
          max={field.max}
          disabled={ctx.disabled}
          onAdd={() => ctx.onChange(appendItem(ctx.value, path, field.blank()))}
          onRemove={(index) => ctx.onChange(removeItem(ctx.value, path, index))}
          onMove={(index, delta) => ctx.onChange(moveItem(ctx.value, path, index, delta))}
          renderItem={(index) => (
            <div className="field-stack">
              {field.fields.map((child) => (
                <FieldView
                  key={`${path}.${index}.${child.path}`}
                  field={child}
                  prefix={`${path}.${index}`}
                  ctx={ctx}
                />
              ))}
            </div>
          )}
        />
      );

    case 'group': {
      const enabled = raw !== null && raw !== undefined;
      return (
        <div className="field-group">
          <label className="checkbox checkbox-field">
            <input
              type="checkbox"
              checked={enabled}
              disabled={ctx.disabled}
              onChange={(event) => update(event.target.checked ? field.blank() : null)}
            />
            {field.label}
            {field.help ? <HelpTip text={field.help} label={`${field.label} 도움말`} /> : null}
          </label>
          {enabled ? (
            <div className="field-stack field-indent">
              {field.fields.map((child) => (
                <FieldView key={join(path, child.path)} field={child} prefix={path} ctx={ctx} />
              ))}
            </div>
          ) : null}
        </div>
      );
    }
  }
}

// ─────────────────────────────── 조각 ───────────────────────────────

function Labeled({
  label,
  help,
  error,
  count,
  children,
}: {
  label: string;
  help?: string;
  error?: string;
  count?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {help ? <HelpTip text={help} label={`${label} 도움말`} /> : null}
        {/* 글자수 상한은 디자인 제약이다 — 잘린 뒤에 알면 늦다. */}
        {count ? <em className="field-count">{count}</em> : null}
      </span>
      {children}
      {error ? <p className="field-error">{error}</p> : null}
    </label>
  );
}

/** 추가·삭제·순서 변경이 있는 목록. 문자열·객체 목록이 공유한다. */
function ListEditor({
  label,
  help,
  itemLabel,
  count,
  max,
  disabled,
  onAdd,
  onRemove,
  onMove,
  renderItem,
}: {
  label: string;
  help?: string;
  itemLabel: string;
  count: number;
  max: number;
  disabled: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMove: (index: number, delta: number) => void;
  renderItem: (index: number) => ReactNode;
}): ReactNode {
  const indices = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);

  return (
    <div className="field-list">
      <div className="field-list-head">
        <span className="field-label">
          {label}
          {help ? <HelpTip text={help} label={`${label} 도움말`} /> : null}
          <em className="field-count">
            {count} / {max}
          </em>
        </span>
        <button type="button" className="btn btn-tiny" disabled={disabled || count >= max} onClick={onAdd}>
          + {itemLabel} 추가
        </button>
      </div>

      {count === 0 ? (
        <p className="field-empty">항목이 없습니다. 화면에 표시되지 않습니다.</p>
      ) : null}

      {indices.map((index) => (
        <div key={index} className="field-item">
          <div className="field-item-head">
            <span className="field-item-num">
              {itemLabel} {index + 1}
            </span>
            <div className="field-item-actions">
              <button
                type="button"
                className="link"
                aria-label="위로"
                disabled={disabled || index === 0}
                onClick={() => onMove(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="link"
                aria-label="아래로"
                disabled={disabled || index === count - 1}
                onClick={() => onMove(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="link link-danger"
                disabled={disabled}
                onClick={() => onRemove(index)}
              >
                제거
              </button>
            </div>
          </div>
          {renderItem(index)}
        </div>
      ))}
    </div>
  );
}

/** 선택된 아이콘을 그대로 보여준다 — 이름만으로는 뭔지 알 수 없다. */
function IconGlyph({ name }: { name: IconName }): ReactNode {
  const svg = useMemo(() => {
    try {
      return renderIcon(name);
    } catch {
      return '';
    }
  }, [name]);

  // core 가 만든 정적 SVG 문자열이다 — 사용자 입력이 아니다.
  return <span className="icon-glyph" aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />;
}

// ─────────────────────────────── 변환 ───────────────────────────────

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function lengthOf(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function countOf(value: unknown, max?: number): string | undefined {
  if (max === undefined) return undefined;
  return `${asString(value).length} / ${max}`;
}

/** `datetime-local` 은 로컬 시각 문자열을 요구한다. ISO 를 그대로 넣으면 비어 보인다. */
function toLocalInput(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * 쉼표로 끊어 배열로.
 *
 * 숫자 계열에서 빈 칸·글자는 0 으로 떨어뜨린다 — 입력 도중의 `"40, "` 같은 중간
 * 상태에서 폼이 터지면 안 되고, 최종 검증은 저장 시 스키마가 한다.
 */
export function parseCsv(input: string, numeric: boolean): Array<string | number> {
  const parts = input
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return numeric ? parts.map((part) => (Number.isFinite(Number(part)) ? Number(part) : 0)) : parts;
}
